/**
 * Real ClearAdvance case files. No fabricated metrics — only what was
 * built, what it was designed to do, and the business goal.
 */

export const CASE_STUDIES = {
    'zubite-platform': {
        id: 'A',
        slug: 'zubite-platform',
        liveUrl: 'https://zubite.bg',
        category: 'Custom Web App · Healthcare Platform',
        title: 'Zubite.bg — Dental Decision Platform',
        oneLiner:
            'A patient-facing decision platform that walks visitors through a quick orthodontic self-assessment and routes them toward the right next step.',
        image: 'https://customer-assets.emergentagent.com/job_82ef164e-2964-47e3-9ebd-49fe28911cdb/artifacts/ouj6f3my_zubite.bg%20storefront.png',
        meta: { fileNo: '0188', class: 'PLATFORM', region: 'BG', year: '2025', status: 'LIVE' },
        built: [
            'Symptom self-assessment',
            'Orthodontic stage quiz',
            'Educational blog system',
            'Clinic profile model',
            'Lead routing logic',
        ],
        goal:
            'Create a scalable, trust-based lead generation platform for dental categories — so patients arrive informed and providers receive qualified intent.',
        sections: [
            {
                heading: 'The brief',
                body: [
                    'Most patients searching for orthodontic information land in two unhelpful places: clinic homepages that talk about the clinic, or generic articles that never lead anywhere. Zubite.bg was designed to sit in front of that decision.',
                    'The brief was to build a platform that educates the patient first, identifies which orthodontic stage they may be at, and only then routes them toward a clinic — turning a vague search into a clear next step.',
                ],
            },
            {
                heading: 'What we built',
                blocks: [
                    {
                        h: 'A patient-facing self-assessment',
                        b: 'A short, plain-language quiz that asks the right questions about visible signs, history, and goals. The result is not a diagnosis — it is a clear stage indicator with a recommended next action.',
                    },
                    {
                        h: 'A trust-led editorial layer',
                        b: 'Symptom pages, orthodontia explainers, and a blog system. Each page is built to answer one question fully, then offer one obvious next step.',
                    },
                    {
                        h: 'Clinic profile model',
                        b: 'A structured way to represent clinics — speciality, location, photos, intake — so patient intent can be matched to the right provider rather than the loudest one.',
                    },
                    {
                        h: 'Lead routing',
                        b: 'When a patient finishes the assessment, their answers are turned into a structured lead with a stage label and routed appropriately. No black-box scoring, no manufactured urgency.',
                    },
                ],
            },
            {
                heading: 'Design notes',
                body: [
                    'The visual direction is calm and clinical, not promotional — large serif statements ("Около 75% от хората имат криви зъби."), clean blue accent for action, and zero stock photography of smiling models.',
                    'The CTA is purposefully soft on the homepage ("Провери къде се намираш" — Check where you are) and only escalates after the assessment, when the patient knows what they are saying yes to.',
                ],
            },
            {
                heading: 'What it is structured to do',
                body: [
                    'Position Zubite.bg as the trusted first stop in the Bulgarian orthodontic decision journey, build an inventory of educational content that compounds over time, and feed clinics qualified — not cold — patient enquiries.',
                ],
            },
        ],
    },

    'cutcraft-racing': {
        id: 'B',
        slug: 'cutcraft-racing',
        liveUrl: 'https://racing.cutcraft.bg',
        category: 'E-Commerce · F1 Collectibles',
        title: 'CutCraft Racing — F1 Storefront',
        oneLiner:
            'A racing-themed storefront for hand-made, 3D-printed F1 accessories, LED lamps, and collector items — built to feel like race day.',
        image: 'https://customer-assets.emergentagent.com/job_82ef164e-2964-47e3-9ebd-49fe28911cdb/artifacts/dlnyky7w_CutCraft%20Racing%20front%20page.png',
        meta: { fileNo: '0212', class: 'COMMERCE', region: 'BG', year: '2025', status: 'LIVE' },
        built: [
            'Conversion-first homepage',
            'Product catalogue & filters',
            'F1 Tracks 2026 capsule',
            'LED Lamps category',
            'Cart & checkout flow',
        ],
        goal:
            'Turn F1 fans into returning customers and present the 2026 season as a curated, season-long catalogue.',
        sections: [
            {
                heading: 'The brief',
                body: [
                    'CutCraft Racing already had a maker engine — a workshop turning out hand-printed F1 collectibles and LED pieces. What it did not have was a storefront that matched the energy of the products themselves.',
                    'The brief was to build a site that feels like the sport: fast, bold, slightly cinematic — and structured around two product worlds (everyday store + the F1 Tracks 2026 capsule) without confusing the buyer.',
                ],
            },
            {
                heading: 'What we built',
                blocks: [
                    {
                        h: 'A homepage built for first impression',
                        b: 'Cinematic blurred-track hero, oversized "ПРЕНЕСИ RACE DAY ВКЪЩИ" headline, and two purposeful CTAs: browse the store, or jump straight to the 2026 calendar capsule.',
                    },
                    {
                        h: 'Two product surfaces in one storefront',
                        b: 'A general shop (LED lamps, accessories, collectibles) sitting alongside the F1 Tracks 2026 calendar — a release-by-release capsule structured around the season.',
                    },
                    {
                        h: 'A 2026 calendar capsule',
                        b: 'Each Grand Prix gets a dedicated entry. New track designs drop on a schedule, giving fans a reason to come back through the year.',
                    },
                    {
                        h: 'Cart, checkout & post-purchase',
                        b: 'A trimmed-down cart with persistent badge, fast checkout, and a clear path back to the catalogue afterwards.',
                    },
                ],
            },
            {
                heading: 'Design notes',
                body: [
                    'Black and signal red, with motion-blur background plates and an angled, racing-livery button silhouette. Bulgarian Cyrillic display type is used at scale — it had to feel like motorsport branding, not generic e-commerce.',
                    'Crucially, the homepage does not try to sell every product. It sells the feeling first ("Race day at home"), then offers two confident ways in.',
                ],
            },
            {
                heading: 'What it is structured to do',
                body: [
                    'Increase repeat visits via the seasonal calendar drop, lift average order value through bundled accessories, and build a long-term collector audience around the 2026 season.',
                ],
            },
        ],
    },

    'cutcraft-studio': {
        id: 'C',
        slug: 'cutcraft-studio',
        liveUrl: 'https://cutcraft.bg',
        category: 'E-Commerce · Personalised Decor',
        title: 'CutCraft Studio — Personalised Home & Kids',
        oneLiner:
            'A premium e-commerce home for personalised 3D-printed and laser-cut products designed and made in Bulgaria.',
        image: 'https://customer-assets.emergentagent.com/job_82ef164e-2964-47e3-9ebd-49fe28911cdb/artifacts/6bmfmws7_cutcraft%20storefront.png',
        meta: { fileNo: '0196', class: 'COMMERCE', region: 'BG', year: '2025', status: 'LIVE' },
        built: [
            'Storefront homepage',
            'Category architecture',
            'Product cards',
            'Trust + shipping module',
            'About / Contacts pages',
        ],
        goal:
            'Position the studio as a premium Bulgarian maker of personalised decor and toys — and reduce buying friction along the way.',
        sections: [
            {
                heading: 'The brief',
                body: [
                    'CutCraft Studio makes personalised decor and toys for homes and children — work that is genuinely premium, but easy to undersell on a generic e-commerce template.',
                    'The brief was to build a storefront that lets the work be the hero, while making it obvious why this studio is different: made in Bulgaria, made by hand, made for one customer at a time.',
                ],
            },
            {
                heading: 'What we built',
                blocks: [
                    {
                        h: 'Editorial-style storefront',
                        b: 'A dark navy hero with one strong product photo, one clear value statement, and a single yellow "Пазарувай тук" CTA. No clutter, no carousel that nobody asked for.',
                    },
                    {
                        h: 'Category architecture',
                        b: 'A grouped catalogue (Decor, Kids, Personalised, Lighting…) so visitors can land on the right shelf in one click instead of scrolling a wall of products.',
                    },
                    {
                        h: 'Trust + shipping module',
                        b: 'A persistent strip surfacing the things buyers actually care about: 30-day money-back guarantee, free shipping over €30, made-in-Bulgaria provenance. Stated, not hidden.',
                    },
                    {
                        h: 'About / Contacts pages',
                        b: 'Short, honest pages that introduce the studio, the process, and the people — so a customer ordering a personalised piece knows who they are talking to.',
                    },
                ],
            },
            {
                heading: 'Design notes',
                body: [
                    'Dark navy with a single warm yellow accent for action — a confident, gallery-like tone rather than the typical busy e-commerce look.',
                    'Personalised work is photographed in real homes, not on white seamless. It treats the products as objects in a life, which is what they actually are.',
                ],
            },
            {
                heading: 'What it is structured to do',
                body: [
                    'Lift the perceived value of every product, make personalised orders feel safe (return policy + provenance up front), and turn one-off buyers into people who come back for the next gift.',
                ],
            },
        ],
    },

    'detetodisha': {
        id: 'D',
        slug: 'detetodisha',
        liveUrl: 'https://detetodisha.bg',
        category: 'Product Site · Educational Health',
        title: 'Здравословно Дишане — Healthy Breathing for Kids',
        oneLiner:
            'A focused product site for a children\'s breathing-awareness kit — a parent handbook plus daily practice cards.',
        image: 'https://customer-assets.emergentagent.com/job_82ef164e-2964-47e3-9ebd-49fe28911cdb/artifacts/tyldp7mc_detetodisha%20storefront.png',
        meta: { fileNo: '0204', class: 'PRODUCT', region: 'BG', year: '2025', status: 'LIVE' },
        built: [
            'Educational product hero',
            'Symptoms checklist',
            'FAQ section',
            'Order flow',
            'Parent-friendly copy system',
        ],
        goal:
            'Help parents recognise early signs of breathing issues — and turn that awareness into a confident purchase.',
        sections: [
            {
                heading: 'The brief',
                body: [
                    'The product is a serious one: a parent handbook and daily-practice cards for children aged 3–12 who may have early signs of mouth-breathing or related issues. The challenge was that most parents do not search for the product — they search for the symptom.',
                    'The brief was to build a single-product site that meets parents at the symptom ("Спи ли детето ви с отворена уста?" — "Does your child sleep with their mouth open?") and walks them through awareness, education, and ordering — without sounding like a sales page.',
                ],
            },
            {
                heading: 'What we built',
                blocks: [
                    {
                        h: 'Educational hero',
                        b: 'A symptom-led headline that names exactly the worry a parent might have. Not a slogan. The product photograph sits beside it as a quiet promise of help.',
                    },
                    {
                        h: 'Symptoms checklist',
                        b: 'A short, honest list of what is normal and what is not. Designed to reassure where it should and to flag where it should — never to scare.',
                    },
                    {
                        h: 'FAQ section',
                        b: 'A real FAQ — written from actual parent questions — covering age range, daily practice, what the cards are, and what the handbook covers.',
                    },
                    {
                        h: 'Order flow',
                        b: 'One clear "Поръчай сега" path. Pricing, contents, and what arrives in the box — all visible before the purchase decision, not after.',
                    },
                    {
                        h: 'Parent-friendly copy system',
                        b: 'A consistent voice throughout: warm, plain, never alarmist. Every section is written so a tired parent at 11pm can read it once and know what to do.',
                    },
                ],
            },
            {
                heading: 'Design notes',
                body: [
                    'Soft green palette — calm, clinical-adjacent, not childish. Editorial serif for headlines, supportive sans-serif for body. Real product photography, no illustration that pretends the product is something else.',
                ],
            },
            {
                heading: 'What it is structured to do',
                body: [
                    'Capture symptom-led search traffic, educate parents in their own time, and turn awareness into a single, confident order — without resorting to fear-based copy.',
                ],
            },
        ],
    },
};

/** Used for the featured-systems list on the home page (in display order). */
export const FEATURED_SLUGS = [
    'zubite-platform',
    'cutcraft-racing',
    'cutcraft-studio',
    'detetodisha',
];
